import { gettext } from '@eventbrite/i18n';
import React, { useRef, useState, type ReactNode } from 'react';
import { DrawerContent } from '../../ScrollableDrawer/DrawerContent';
import { ScrollableDrawer } from '../../ScrollableDrawer/ScrollableDrawer';
import { CategoryBadge } from './CategoryBadge';

import { STATSIG_EVENT_NAMES } from '../../../experimentation';

import { useIntersecting } from '../../hooks/useIntersecting';
import {
    EventDetailsMainInner,
    EventDetailsSectionTitle,
} from '../EventDetails';
import { useLazyImagePlaceholder } from './hooks';

import { LISTING_HEAP_IDS } from '../../../constants';
import { useTrackComponent } from '../../hooks/useTrackComponent';
import { ExpandingButtonLink } from '../../_shared/ButtonLink';
import './EventDescription.scss';
import type {
    ImageContentProps,
    OverviewProps,
    StructuredContentModule,
    TextContentProps,
    VideoContentProps,
} from './types';
import { StructuredContentModuleType, TextContentAlignment } from './types';

const getTextAlignment = (alignment: TextContentAlignment) => {
    switch (alignment) {
        case TextContentAlignment.LEFT:
            return 'eds-text--left';
        case TextContentAlignment.CENTER:
            return 'eds-text--center';
        case TextContentAlignment.RIGHT:
            return 'eds-text--right';
    }
};

const ContentWrapper = ({
    style,
    children,
}: {
    style?: string;
    children?: ReactNode;
}) => {
    const className = `${style}`;

    return <div className={className}>{children}</div>;
};

const TextContent = ({ text, alignment }: TextContentProps) => (
    <ContentWrapper style="structured-content-rich-text">
        <div
            className={getTextAlignment(alignment)}
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{ __html: text }}
        />
    </ContentWrapper>
);

const LazyContent: React.FC<{ fallback: ReactNode }> = ({
    children,
    fallback,
}) => {
    const LAZY_ELEMENT_ROOT_MARGIN = '100px';
    const lazyElememt = useRef(null);

    const [isIntersecting] = useIntersecting({
        ref: lazyElememt,
        rootMargin: LAZY_ELEMENT_ROOT_MARGIN,
    });

    return (
        <div ref={lazyElememt}>
            {isIntersecting ? (
                <div className="eds-fx--fade-in">{children}</div>
            ) : (
                fallback
            )}
        </div>
    );
};

const VideoContent = ({ url, altText }: VideoContentProps) => (
    <ContentWrapper style="structured-content-video">
        <iframe
            className="structured-content-video__iframe"
            src={url}
            frameBorder="0"
            allowFullScreen
            title={altText || gettext('Video content').toString()}
            loading="lazy"
        >
            {altText}
        </iframe>
    </ContentWrapper>
);

const ImageContent = ({
    url,
    altText,
    captionText,
    width = 0,
    height = 0,
}: ImageContentProps) => {
    const fallback = useLazyImagePlaceholder(url, 'event-description', {
        width,
        height,
    });

    return (
        <ContentWrapper>
            <LazyContent fallback={fallback}>
                <img
                    className="eds-max-img"
                    src={url}
                    alt={altText || ''}
                    loading="lazy"
                />
            </LazyContent>
            {captionText && (
                <span className="eds-text-bs eds-text-color--ui-600">
                    {captionText}
                </span>
            )}
        </ContentWrapper>
    );
};

const StructuredModuleRenderer = ({
    module,
    isPreview,
}: {
    module: StructuredContentModule;
    isPreview?: boolean;
}) => {
    switch (module.type) {
        case StructuredContentModuleType.TEXT:
            const textContent = module as TextContentProps;
            return (
                <TextContent
                    text={textContent.text}
                    alignment={textContent.alignment}
                />
            );
        case StructuredContentModuleType.IMAGE:
            const imageContent = module as ImageContentProps;
            return (
                <div
                    className={
                        isPreview ? 'structure-content-item--hidden' : ''
                    }
                    data-testid="image-content"
                >
                    <ImageContent
                        url={imageContent.url}
                        altText={imageContent.altText}
                        captionText={imageContent.captionText}
                        width={imageContent.width}
                        height={imageContent.height}
                    />
                </div>
            );
        case StructuredContentModuleType.VIDEO:
            const videoContent = module as VideoContentProps;
            return (
                <div
                    className={
                        isPreview ? 'structure-content-item--hidden' : ''
                    }
                    data-testid="video-content"
                >
                    <VideoContent
                        url={videoContent.url}
                        altText={videoContent.altText}
                    />
                </div>
            );
        default:
            return <></>;
    }
};

const ReadMoreButton = ({
    expanded,
    onClick,
}: {
    expanded: boolean;
    onClick: () => void;
}) => {
    const heapId = expanded
        ? LISTING_HEAP_IDS.ABOUT_THIS_EVENT_READ_LESS
        : LISTING_HEAP_IDS.ABOUT_THIS_EVENT_READ_MORE;

    const text = expanded ? gettext('Show less') : gettext('Read more');

    return (
        <ExpandingButtonLink
            aria-controls="event-description"
            heapId={heapId}
            onClick={onClick}
            text={text}
        />
    );
};

const useOverviewTracking = (ref: React.RefObject<HTMLDivElement>) => {
    useTrackComponent({
        ref: ref,
        heap: {
            eventName: LISTING_HEAP_IDS.ABOUT_THIS_EVENT_VIEW,
            eventBucketLabel:
                'Views on the About this event section (top of the section)',
        },
        statsig: {
            eventName: STATSIG_EVENT_NAMES.ABOUT_THIS_EVENT_VIEW,
        },
    });
};

const useOverviewState = () => {
    const [expanded, setExpanded] = useState<boolean>(false);
    const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false);
    const ref = useRef<HTMLDivElement>(null);

    return { expanded, setExpanded, isDialogOpen, setIsDialogOpen, ref };
};

interface OverviewHandlersConfig {
    expanded: boolean;
    setExpanded: (value: boolean) => void;
    setIsDialogOpen: (value: boolean) => void;
    ref: React.RefObject<HTMLDivElement>;
    isMobile: boolean;
}

const useOverviewHandlers = (config: OverviewHandlersConfig) => {
    const { expanded, setExpanded, setIsDialogOpen, ref, isMobile } = config;

    const handleMobileReadMore = () => setIsDialogOpen(true);

    const handleDesktopReadMore = () => {
        if (expanded && ref.current?.scrollIntoView) {
            ref.current.scrollIntoView({ behavior: 'smooth' });
        }
        setExpanded(!expanded);
    };

    const onReadMoreClick = () => {
        if (isMobile) {
            handleMobileReadMore();
        } else {
            handleDesktopReadMore();
        }
    };

    const handleCloseDialog = () => setIsDialogOpen(false);

    return { onReadMoreClick, handleCloseDialog };
};

const isMediaModule = (module: StructuredContentModule): boolean => {
    return (
        module.type === StructuredContentModuleType.IMAGE ||
        module.type === StructuredContentModuleType.VIDEO
    );
};

const getFirstTextModuleIndex = (
    modules: StructuredContentModule[],
): number => {
    return modules.findIndex(
        (m) => m.type === StructuredContentModuleType.TEXT,
    );
};

const shouldShowInCollapsedWithSummary = (
    module: StructuredContentModule,
    firstModule: StructuredContentModule,
): boolean => {
    if (isMediaModule(firstModule)) {
        return false;
    }
    return !isMediaModule(module);
};

const shouldShowInCollapsedWithoutSummary = (
    module: StructuredContentModule,
    moduleIndex: number,
    modules: StructuredContentModule[],
): boolean => {
    const firstTextIndex = getFirstTextModuleIndex(modules);

    if (firstTextIndex === -1) {
        return true;
    }

    if (moduleIndex < firstTextIndex) {
        return false;
    }

    return !isMediaModule(module);
};

const shouldShowModuleInCollapsed = (
    module: StructuredContentModule,
    moduleIndex: number,
    modules: StructuredContentModule[],
    summary: string,
    expanded: boolean,
): boolean => {
    if (expanded) {
        return true;
    }

    const hasSummary = Boolean(summary);
    const firstModule = modules[0];

    if (hasSummary) {
        return shouldShowInCollapsedWithSummary(module, firstModule);
    }

    return shouldShowInCollapsedWithoutSummary(module, moduleIndex, modules);
};

const useContentLogic = (
    summary: string,
    modules: StructuredContentModule[],
) => {
    const hasLongContent = () => {
        const hasLongSummary = summary && summary.length > 280;
        const hasLongTextModule = modules.some(
            (module) =>
                module.type === StructuredContentModuleType.TEXT &&
                (module as TextContentProps).text?.length > 280,
        );
        const hasMultipleModules = modules.length > 1;

        const firstModule = modules[0];
        const hasSummary = Boolean(summary);
        const isFirstModuleMedia =
            firstModule &&
            (firstModule.type === StructuredContentModuleType.IMAGE ||
                firstModule.type === StructuredContentModuleType.VIDEO);

        const hasHiddenModules =
            hasSummary && isFirstModuleMedia && modules.length > 0;

        return (
            hasLongSummary ||
            hasLongTextModule ||
            hasMultipleModules ||
            hasHiddenModules
        );
    };

    return { needsReadMore: hasLongContent() };
};

const OverviewHeader = React.forwardRef<HTMLHeadingElement>((_props, ref) => (
    <EventDetailsSectionTitle>
        <h2 ref={ref}>{gettext('Overview')}</h2>
    </EventDetailsSectionTitle>
));

const OverviewContent = ({
    summary,
    modules,
    expanded,
    showFadeOut,
}: {
    summary: string;
    modules: StructuredContentModule[];
    expanded: boolean;
    showFadeOut: boolean;
}) => (
    <div className={`has-user-generated-content event-description`}>
        <div className="event-description__content-wrapper">
            <div
                className={`has-user-generated-content event-description__content ${
                    !expanded
                        ? 'event-description__content--collapsed'
                        : 'event-description__content--expanded'
                }`}
                id="event-description"
            >
                {summary && (
                    <ContentWrapper>
                        <div className="summary">{summary}</div>
                    </ContentWrapper>
                )}
                {modules.map((module, index) => {
                    const shouldShow = shouldShowModuleInCollapsed(
                        module,
                        index,
                        modules,
                        summary,
                        expanded,
                    );

                    if (!shouldShow) {
                        return null;
                    }

                    return (
                        <StructuredModuleRenderer
                            module={module}
                            key={index}
                            isPreview={!expanded}
                        />
                    );
                })}
            </div>
            {!expanded && showFadeOut && (
                <div className="event-description__fade-out" />
            )}
        </div>
    </div>
);

const OverviewActions = ({
    needsReadMore,
    expanded,
    onReadMoreClick,
}: {
    needsReadMore: boolean;
    expanded: boolean;
    onReadMoreClick: () => void;
}) => {
    if (!needsReadMore) return null;

    return <ReadMoreButton expanded={expanded} onClick={onReadMoreClick} />;
};

const OverviewDialogWrapper = ({
    isDialogOpen,
    closeModal,
    summary,
    modules,
}: {
    isDialogOpen: boolean;
    closeModal: () => void;
    summary: string;
    modules: StructuredContentModule[];
}) => {
    if (!isDialogOpen) return null;

    return (
        <OverviewDialog
            closeModal={closeModal}
            summary={summary}
            modules={modules}
        />
    );
};

export const Overview = ({
    modules,
    summary,
    category,
    subcategory,
    isMobile = false,
}: OverviewProps & { isMobile?: boolean }) => {
    const { expanded, setExpanded, isDialogOpen, setIsDialogOpen, ref } =
        useOverviewState();
    const { onReadMoreClick, handleCloseDialog } = useOverviewHandlers({
        expanded,
        setExpanded,
        setIsDialogOpen,
        ref,
        isMobile,
    });
    const { needsReadMore } = useContentLogic(summary, modules);

    useOverviewTracking(ref);

    if (modules.length === 0 && !summary) {
        return null;
    }

    return (
        <EventDetailsMainInner>
            <div className="structured-content">
                <OverviewHeader ref={ref} />
                <OverviewContent
                    summary={summary}
                    modules={modules}
                    expanded={expanded}
                    showFadeOut={needsReadMore}
                />
                <OverviewActions
                    needsReadMore={needsReadMore}
                    expanded={expanded}
                    onReadMoreClick={onReadMoreClick}
                />
                <CategoryBadge category={category} subcategory={subcategory} />
            </div>
            <OverviewDialogWrapper
                isDialogOpen={isDialogOpen}
                closeModal={handleCloseDialog}
                summary={summary}
                modules={modules}
            />
        </EventDetailsMainInner>
    );
};

const OverviewDialog = ({
    closeModal,
    summary,
    modules,
}: {
    closeModal: () => void;
    summary: string;
    modules: StructuredContentModule[];
}) => {
    const expanded = true;

    return (
        <ScrollableDrawer
            onClickOutside={closeModal}
            showCloseButton={true}
            isScrollUpAllowed={false}
        >
            <DrawerContent>
                <div className="overview-drawer-content">
                    <EventDetailsSectionTitle>
                        <h2>{gettext('Overview')}</h2>
                    </EventDetailsSectionTitle>
                    <div className="has-user-generated-content event-description">
                        {summary && (
                            <ContentWrapper>
                                <div className="summary">{summary}</div>
                            </ContentWrapper>
                        )}
                        {modules.map((module, index) => {
                            const shouldShow = shouldShowModuleInCollapsed(
                                module,
                                index,
                                modules,
                                summary,
                                expanded,
                            );

                            if (!shouldShow) {
                                return null;
                            }

                            return (
                                <StructuredModuleRenderer
                                    module={module}
                                    key={index}
                                    isPreview={false}
                                />
                            );
                        })}
                    </div>
                </div>
            </DrawerContent>
        </ScrollableDrawer>
    );
};
